<script lang="ts" setup>
import { onMounted, onUnmounted, ref, unref, useNuxtApp, watch } from '#imports';

interface Props {
    isOpen: boolean;
    dropdownClass?: string;
    dropdownContentClass?: string;
}

defineOptions({
    inheritAttrs: false
});

const TIME_ANIMATION = 0.3;
const EASE_ANIMATION = 'power2.out';

const refParent = ref<HTMLElement | undefined>(undefined);
const refContent = ref<Nullable<HTMLElement>>(null);

const emit = defineEmits(['update:isOpen']);

const props = withDefaults(
    defineProps<Props>(),
    {
        dropdownClass: undefined,
        dropdownContentClass: undefined,
        initialVisibility: false
    }
);

const { '$gsap': gsap } = useNuxtApp();
const ctx = ref<gsap.Context>();
const ddMenu = ref<GSAPTimeline>();

// убираем реактивность, что бы при повторных вызовах функции createAnimation, не юзать изменившейся пропс.
const initialIsOpen = unref(props.isOpen);

/*
 * функция для формирования стилей, значения как строки, т.к. :style в иделе нужны строки, а джисапу похуй
 * подкидываем на сервере эти стили, и на клиенте тоже что бы не было ошибки гидротации
 */
function getStyles(isOpen: boolean) {
    return {
        height: isOpen
            ? 'auto'
            : '0px',
        opacity: isOpen
            ? '1'
            : '0'
    };
}

function createAnimation() {
    // set на всякий случай, что бы джисап подменил инлаин стили на свои (вдруг между сервером и клиентом случится собитие которое закроет, то мы сразу подкинем актуальное состояние)
    gsap.set(
        refContent.value,
        getStyles(initialIsOpen)
    );
    ctx.value = gsap.context(
        () => {
            ddMenu.value = gsap
                ?.timeline({
                    defaults: {
                        duration: TIME_ANIMATION,
                        ease: EASE_ANIMATION
                    }
                })
            // состояние на оборт от иницилизируемого
                .to(
                    refContent.value,
                    getStyles(!initialIsOpen)
                )
                .reverse();
        },
        refParent.value
    );
}

watch(
    () => props.isOpen,
    () => {
        /*
       * если смущает вызов функции в функции, то это не баг, это фича, есть в доках gsap, это как бы перевернутая анимация
       * flips the orientation using the reversed() method instead:
       * myAnimation.reversed(!myAnimation.reversed());
       * https://gsap.com/docs/v3/GSAP/Tween/reverse()/
       */
        ddMenu.value?.reversed(!ddMenu.value?.reversed());
    }
);

onMounted(() => {
    createAnimation();
});

onUnmounted(() => {
    ctx.value?.revert();
    ctx.value = undefined;
    ddMenu.value?.kill();
});
</script>

<template>
  <div ref="refParent"
       class="panel"
       :class="dropdownClass"
       v-bind="$attrs"
  >
    <slot name="toggle"></slot>

    <div ref="refContent"
         :style="{...getStyles(initialIsOpen)}"
         :class="dropdownContentClass"
         @click.stop
    >
      <slot name="content"></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.panel {
  overflow: hidden;
}
</style>
